/**
 * Copyright (c) 2024-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { type HTMLArkProps, ark } from "@ark-ui/react/factory";
import { styled } from "@ndla/styled-system/jsx";
import type { StyledProps } from "@ndla/styled-system/types";
import { forwardRef, useEffect } from "react";

export interface ExpandableBoxProps extends HTMLArkProps<"details">, StyledProps {}

const StyledExpandableBox = styled(
  ark.details,
  {
    base: {
      transitionDuration: "fast",
      width: "full",
      position: "relative",
      border: "1px solid",
      borderRadius: "xsmall",
      borderColor: "stroke.subtle",
      padding: "medium",
      clear: "both",
      overflow: "hidden",
      _open: {
        padding: "medium",
        "& summary, [data-embed-type='expandable-box-summary']": {
          marginBlockEnd: "-xxsmall",
        },
      },
    },
  },
  { baseComponent: true },
);

const onBeforePrint = (_: Event) => {
  const detailsElements = document.querySelectorAll("details");
  detailsElements.forEach((details) => {
    if (!details.hasAttribute("open")) {
      details.setAttribute("data-print-open", "true");
      details.setAttribute("open", "true");
    }
  });
};

const onAfterPrint = (_: Event) => {
  const detailsElements = document.querySelectorAll("details[data-print-open]");
  detailsElements.forEach((details) => {
    details.removeAttribute("data-print-open");
    details.removeAttribute("open");
  });
};

export const ExpandableBox = forwardRef<HTMLDetailsElement, ExpandableBoxProps>((props, ref) => {
  useEffect(() => {
    window.addEventListener("beforeprint", onBeforePrint);
    window.addEventListener("afterprint", onAfterPrint);
    return () => {
      window.removeEventListener("beforeprint", onBeforePrint);
      window.removeEventListener("afterprint", onAfterPrint);
    };
  }, []);
  return <StyledExpandableBox {...props} data-embed-type="expandable-box" ref={ref} />;
});

export interface ExpandableBoxSummaryProps extends HTMLArkProps<"summary">, StyledProps {}

const StyledExpandableBoxSummary = styled(
  ark.summary,
  {
    base: {
      cursor: "pointer",
      margin: "-medium",
      padding: "medium",
      textStyle: "label.large!",
      _hover: {
        color: "text.action",
      },
      "& > *": {
        display: "inline!",
        textStyle: "label.large!",
      },
    },
  },
  { baseComponent: true },
);

export const ExpandableBoxSummary = forwardRef<HTMLElement, ExpandableBoxSummaryProps>((props, ref) => (
  <StyledExpandableBoxSummary {...props} data-embed-type="expandable-box-summary" ref={ref} />
));
